import { useId, useSyncExternalStore } from "react";
import type { Character } from "./characters.js";
import { CHARACTER_LOOK, characterPaint, MOUTHS } from "./characters.js";

const BLINK_SECONDS = 5.6;
const LOOK_SECONDS = 8;

// Where the pupils rest and where they glance, as fractions of one look cycle.
const LOOK_TIMES = "0;0.16;0.24;0.4;0.48;0.64;0.72;0.88;1";
const LOOK_SHIFTS = "0 0;0 0;-2.6 0.4;-2.6 0.4;2.6 -1.2;2.6 -1.2;0 2;0 2;0 0";
const LOOK_EASING = Array(8).fill("0.45 0 0.25 1").join(";");
const BLINK_TIMES = "0;0.9;0.94;0.98;1";
const BLINK_SCALES = "1 1;1 1;1 0.1;1 1;1 1";

const reducedMotionMedia = "(prefers-reduced-motion: reduce)";

function subscribeToReducedMotion(onChange: () => void): () => void {
  const media = window.matchMedia(reducedMotionMedia);
  media.addEventListener("change", onChange);
  return () => media.removeEventListener("change", onChange);
}

function reducedMotionSnapshot(): boolean {
  return window.matchMedia(reducedMotionMedia).matches;
}

export type BotCharacterProps = {
  character: Character;
  /** Body colour as a hex value. */
  color: string;
  size?: number;
  className?: string;
  /**
   * Offsets this character's blink and glance so a row of bots never moves in unison.
   * Any number works; the default keeps each character on its own rhythm.
   */
  phase?: number;
  /** Draw a still face. Also the result whenever the visitor prefers reduced motion. */
  still?: boolean;
};

/**
 * One library character: a soft body in `color` and eyes that glance around and blink.
 *
 * The motion is SVG animation inside the image, so it needs no stylesheet and behaves the same
 * in a Vite app and a statically rendered page.
 */
export function BotCharacter({
  character,
  color,
  size = 38,
  className,
  phase,
  still = false,
}: BotCharacterProps) {
  const id = `bot-character-${useId().replace(/[^a-zA-Z0-9-_]/g, "")}`;
  const reducedMotion = useSyncExternalStore(
    subscribeToReducedMotion,
    reducedMotionSnapshot,
    () => false,
  );
  const animated = !still && !reducedMotion;
  const offset = phase ?? 0;
  const blinkBegin = `${-(offset * 1.3).toFixed(2)}s`;
  const lookBegin = `${-(offset * 2.1).toFixed(2)}s`;
  const look = CHARACTER_LOOK;
  const paint = characterPaint(color);
  const mouth = MOUTHS[character.mouth];
  const cheekY = character.eyeY + look.cheek.dy;

  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={className}
      style={{ display: "block", flex: "none", overflow: "visible" }}
      aria-hidden="true"
      data-character={character.id}
    >
      <defs>
        <clipPath id={`${id}-body`}>
          <path d={character.path} />
        </clipPath>
        <radialGradient
          id={`${id}-shade`}
          gradientUnits="userSpaceOnUse"
          cx={look.body.cx}
          cy={look.body.cy}
          r={look.body.r}
        >
          <stop offset="0" stopColor={paint.light} />
          <stop offset="0.5" stopColor={paint.base} />
          <stop offset="1" stopColor={paint.shadow} />
        </radialGradient>
        <radialGradient id={`${id}-floor`}>
          <stop offset="0" stopColor={paint.deep} stopOpacity={look.floor.opacity} />
          <stop offset="1" stopColor={paint.deep} stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${id}-gloss`}>
          <stop offset="0" stopColor="#fff" stopOpacity={look.gloss.opacity} />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${id}-cheek`}>
          <stop offset="0" stopColor={look.cheek.color} stopOpacity={look.cheek.opacity} />
          <stop offset="1" stopColor={look.cheek.color} stopOpacity="0" />
        </radialGradient>
      </defs>
      <path d={character.path} fill={paint.rim} />
      <g clipPath={`url(#${id}-body)`}>
        <path
          d={character.path}
          fill={`url(#${id}-shade)`}
          transform={`translate(${look.rimShift.x} ${look.rimShift.y})`}
        />
        <ellipse
          cx={look.floor.cx}
          cy={look.floor.cy}
          rx={look.floor.rx}
          ry={look.floor.ry}
          fill={`url(#${id}-floor)`}
        />
        {(character.marks ?? []).map(([x, y, rx, ry]) => (
          <ellipse
            key={`${x}-${y}`}
            cx={x}
            cy={y}
            rx={rx}
            ry={ry}
            fill={look.mark.color}
            fillOpacity={look.mark.opacity}
          />
        ))}
        <ellipse
          cx={look.gloss.cx}
          cy={look.gloss.cy}
          rx={look.gloss.rx}
          ry={look.gloss.ry}
          transform={`rotate(${look.gloss.angle} ${look.gloss.cx} ${look.gloss.cy})`}
          fill={`url(#${id}-gloss)`}
        />
        {[-1, 1].map((side) => (
          <ellipse
            key={side}
            cx={50 + side * (character.eyeGap + look.cheek.dx)}
            cy={cheekY}
            rx={look.cheek.rx}
            ry={look.cheek.ry}
            fill={`url(#${id}-cheek)`}
          />
        ))}
      </g>
      <path
        d={mouth.d}
        transform={`translate(50 ${character.mouthY})`}
        fill={mouth.filled ? paint.deep : "none"}
        stroke={mouth.filled ? "none" : paint.deep}
        strokeWidth={look.mouthWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {[-character.eyeGap, character.eyeGap].map((dx) => (
        <g key={dx} transform={`translate(${50 + dx} ${character.eyeY})`}>
          <g>
            {animated ? (
              <animateTransform
                attributeName="transform"
                type="scale"
                values={BLINK_SCALES}
                keyTimes={BLINK_TIMES}
                dur={`${BLINK_SECONDS}s`}
                begin={blinkBegin}
                repeatCount="indefinite"
              />
            ) : null}
            <ellipse rx={look.eye.rx} ry={look.eye.ry} fill="#fff" />
            <g>
              {animated ? (
                <animateTransform
                  attributeName="transform"
                  type="translate"
                  values={LOOK_SHIFTS}
                  keyTimes={LOOK_TIMES}
                  calcMode="spline"
                  keySplines={LOOK_EASING}
                  dur={`${LOOK_SECONDS}s`}
                  begin={lookBegin}
                  repeatCount="indefinite"
                />
              ) : null}
              <circle r={look.eye.pupil} fill={look.eye.ink} />
              <circle cx={look.glint.x} cy={look.glint.y} r={look.glint.r} fill="#fff" />
              <circle
                cx={look.spark.x}
                cy={look.spark.y}
                r={look.spark.r}
                fill="#fff"
                fillOpacity={look.spark.opacity}
              />
            </g>
          </g>
        </g>
      ))}
    </svg>
  );
}
